import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { getDisplayName } from "@/lib/utils/display-name";
import LinkedText, { type LinkedEntity } from "@/components/shared/LinkedText";
import type { NavigationItem } from "@/components/shared/SheetDrillDown";
import PersonDetailSheet from "@/components/shared/PersonDetailSheet";
import CompanyDetailSheet from "@/components/shared/CompanyDetailSheet";
import AskCoffeePanel from "@/components/shared/AskCoffeePanel";
import { MapPin, Building2, Mail, TrendingUp, ExternalLink, Search, Loader2, Send, Video, Check, Users, ArrowLeft, Sparkles } from "lucide-react";
import LinkedInIcon from "@/components/shared/LinkedInIcon";
import MeetingIntelligenceSheet from "@/components/shared/MeetingIntelligenceSheet";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserAvatar from "@/components/shared/Avatar";
import CompanyLogo from "@/components/shared/CompanyLogo";

import { people, companies } from "@/data/dummy";
import { cn } from "@/lib/utils";
import { useStickyState } from "@/hooks/useStickyState";
import { useGetEngagementBriefLazyQuery, useUpdateMeetingRecordingMutation } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { isLovablePreview } from "@/lib/utils/environment";
import { personEditStore } from "@/lib/utils/person-edit-store";

import type { PartialBlock } from "@blocknote/core";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import { MutationGate, useCanUseMeetingCapture, READ_ONLY_MEETINGS_REASON } from "@/lib/rbac";
import { briefToBlocks } from "@/lib/utils/briefing";
import { useChatAgent } from "@/lib/hooks/use-chat-agent";
import type { ChatContext } from "@/lib/types/chat";
import EngagementPrivacyGate from "@/components/shared/EngagementPrivacyGate";
import RecordingOverrideDialog from "@/components/shared/RecordingOverrideDialog";
import { RECORDING_EXISTS } from "@/lib/recording-override";
import { useTranslation } from "react-i18next";

/**
 * Turns a brief section's backend key ("nextSteps", "deal_context") into a
 * heading. The key is data, not copy, so the words stay as the backend wrote
 * them — this only fixes the casing and separators.
 */
function humanizeSectionKey(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");
}

/**
 * Demo briefing copy shown when a meeting has no generated brief yet. Fixture
 * content in the same class as `src/data/dummy` — deliberately not translated.
 */
function previewBriefingContent(attendees: string[]): Record<string, string> {
  return {
    "Attendee Background": `${attendees.slice(0, 2).join(" and ")} are key contacts. Review their recent activity and role context before the meeting.`,
    "Recent Interactions": `12 emails exchanged in the last 30 days. Last meeting on Feb 27 covered migration timeline and API integration concerns.`,
    "Deal Context": `Active deal worth $150K in Proposal stage. Key risks: legacy API migration timeline and competitor evaluation.`,
    "Company News": `Recently announced Series C funding of $45M. Expanding engineering team by 40% in Q2.`,
    "Competitive Landscape": `Currently evaluating two alternative vendors. Our key differentiators: migration support and API performance.`,
    "Key Risks": `Main risk is the legacy API integration timeline. Secondary concern around budget approval process.`,
    "Talking Points": `1. Confirm migration timeline expectations\n2. Address API integration concerns\n3. Discuss POC staging environment access\n4. Review pricing for 3-year commitment`,
    "Preparation Notes": `Review technical spec document shared after last call. Prepare staging environment credentials. Loop in integration team for endpoint mapping discussion.`,
  };
}

interface Meeting {
  id: string;
  title: string;
  date: string;
  time: string;
  duration: string;
  type: string;
  attendees: string[];
  link?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: Meeting | null;
  isRecording?: boolean;
  onRecordingChange?: (recording: boolean) => void;
  onRecordingSync?: (recording: boolean) => void; // Silent state sync from backend (no toast/mutation)
  hideOverlay?: boolean; // Hide overlay when opened as a nested/stacked sheet
  onBack?: () => void; // Back arrow handler when opened from a drill-down card
}

// Mock background data per attendee
const emailSummaries: Record<string, string> = {
  "Sarah Chen": "12 emails exchanged since Jan 2026. Last thread discussed migration timeline and infrastructure requirements.",
  "Alex Kim": "8 emails exchanged since Feb 2026. Recent topics: API architecture, data security compliance.",
  "James Wilson": "15 emails exchanged since Dec 2025. Focused on contract terms, pricing tiers, and renewal timeline.",
  "Maria Garcia": "5 emails exchanged since Feb 2026. Initial outreach about marketing automation needs.",
  "Nina Rossi": "3 emails exchanged since Feb 2026. Discussed design collaboration and branding requirements.",
  "Priya Patel": "7 emails exchanged since Jan 2026. Conversations about cloud migration roadmap and onboarding.",
  "Tom Baker": "10 emails exchanged since Jan 2026. Topics: logistics dashboard requirements, delivery metrics.",
  "David Okafor": "6 emails exchanged since Feb 2026. Budget discussions and Q2 financial planning.",
};

const meetingTranscripts: Record<string, string> = {
  "Sarah Chen": "Last meeting was Stripe API Workshop: Reviewed Phase 1 migration plan. Sarah raised concerns about downtime windows. Agreed on a staged rollout approach.",
  "Alex Kim": "Last meeting was Datadog Security Audit: Deep dive into Datadog API integration. Alex suggested using their new streaming endpoints for real-time sync.",
  "James Wilson": "Last meeting was Shopify Pricing Discussion: Contract review with legal teams. James requested a 10% volume discount for 3-year commitment.",
  "Maria Garcia": "Last meeting was HubSpot Campaign Wrap-up: Initial discovery call. Maria outlined their current marketing stack and pain points with attribution.",
  "Nina Rossi": "Last meeting was HubSpot Campaign Wrap-up: Reviewed Canva's design system. Nina wants tighter integration with their component library.",
  "Priya Patel": "Last meeting was Notion Integration Check: Discussed workspace expansion requirements and onboarding timeline.",
  "Tom Baker": "Last meeting was Figma Onboarding Prep: Walked through logistics dashboard wireframes. Tom needs real-time driver tracking on mobile.",
  "David Okafor": "Last meeting was Q4 Revenue Review: Discussed Q2 budget allocation. David is looking to consolidate fintech vendors.",
};

const recentChanges: Record<string, string[]> = {
  "Sarah Chen": ["Promoted to VP of Engineering (Jan 2026)", "Stripe raised latest round in Feb 2026"],
  "Alex Kim": ["Datadog launched new enterprise tier", "Alex spoke at CloudConf 2026"],
  "James Wilson": ["Shopify expanded to new markets", "New board member announced"],
  "Maria Garcia": ["HubSpot won Marketing Platform of the Year"],
  "Nina Rossi": ["Canva hired 5 new designers in Q1"],
  "Tom Baker": ["Figma launched new enterprise features"],
  "David Okafor": ["Plaid acquired a small payments startup"],
  "Priya Patel": ["Notion partnered with major enterprise clients"],
};

// Mock Q&A responses based on meeting context
function getMockAnswer(question: string, meeting: Meeting, attendeeNames: string[]): string {
  const q = question.toLowerCase();
  if (/agenda|topic|discuss/i.test(q)) {
    return `Based on previous conversations, the likely agenda includes:\n\n• Review progress on current initiatives\n• Discuss open action items from the last meeting\n• Align on next steps and timeline\n\nAttendees ${attendeeNames.slice(0, 2).join(" and ")} have been most active in recent threads.`;
  }
  if (/risk|concern|worry/i.test(q)) {
    return `Key risks to be aware of:\n\n• Timeline pressure — several attendees have flagged tight deadlines in recent emails\n• Budget alignment — ${attendeeNames[0]} raised questions about Q2 spend in the last thread\n• Resource availability may be a concern based on recent hiring signals`;
  }
  if (/follow.?up|action|next/i.test(q)) {
    return `Outstanding action items from prior interactions:\n\n• Send updated proposal to ${attendeeNames[0]} (pending since last week)\n• ${attendeeNames[1] || attendeeNames[0]} to share technical requirements doc\n• Schedule follow-up review for next sprint`;
  }
  if (/competitor|alternative/i.test(q)) {
    return `Based on CRM data and recent conversations:\n\n• ${attendeeNames[0]} mentioned evaluating 2 alternative vendors\n• Competitive pressure is moderate — focus on differentiation in integration depth and support response time`;
  }
  if (/email|thread|correspondence/i.test(q)) {
    return `Email summary across attendees:\n\n${attendeeNames.filter(n => emailSummaries[n]).map(n => `• **${n}**: ${emailSummaries[n]}`).join("\n")}`;
  }
  if (/record|transcript|last meeting|previous/i.test(q)) {
    return `Previous meeting notes:\n\n${attendeeNames.filter(n => meetingTranscripts[n]).map(n => `• **${n}**: ${meetingTranscripts[n]}`).join("\n")}`;
  }
  return `Here's what I found relevant to "${question}":\n\n• This meeting involves ${attendeeNames.length} attendees from ${new Set(attendeeNames.map(n => people.find(p => p.name === n)?.company).filter(Boolean)).size} companies\n• Key topics from recent interactions include contract discussions, technical requirements, and timeline planning\n• I'd recommend preparing talking points around mutual goals and open action items.`;
}

interface QAMessage {
  id: string;
  role: "user" | "ai";
  text: string;
}

// Internal team member details for briefing attendee cards
const internalTeamDetails: Record<string, { id: string; name: string; title: string; company: string; email: string; phone: string; linkedin: string; location: string; avatar: string }> = {
  "Doug Smith": { id: "int-1", name: "Doug Smith", title: "Sales Director", company: "Gazillion", email: "doug@gazillion.com", phone: "+1 415-555-0100", linkedin: "linkedin.com/in/dougsmith", location: "San Francisco, CA", avatar: "/avatars/doug.jpg" },
  "Lisa Park": { id: "int-2", name: "Lisa Park", title: "Account Executive", company: "Gazillion", email: "lisa@gazillion.com", phone: "+1 415-555-0102", linkedin: "linkedin.com/in/lisapark", location: "San Francisco, CA", avatar: "/avatars/lisa-park.jpg" },
  "Mike Chen": { id: "int-3", name: "Mike Chen", title: "Sales Engineer", company: "Gazillion", email: "mike@gazillion.com", phone: "+1 415-555-0103", linkedin: "linkedin.com/in/mikechen", location: "San Francisco, CA", avatar: "/avatars/mike-chen.jpg" },
};

// AI-generated company intelligence briefs
const companyIntelligence: Record<string, string> = {
  "Stripe": "Stripe is a global financial infrastructure platform that powers online payments for millions of businesses. Their core products include Stripe Payments, Billing, Connect, and Treasury — enabling everything from subscription management to embedded finance. In recent news, Stripe completed a $6.5B funding round in early 2026, and has been aggressively expanding its enterprise sales motion with new compliance and reporting tools aimed at large financial institutions.",
  "Datadog": "Datadog is a leading cloud monitoring and security platform that provides observability across infrastructure, applications, and logs. Their product suite includes APM, real-time dashboards, SIEM, and cloud cost management. Recently, Datadog launched a new AI-powered anomaly detection feature and reported strong Q4 2025 earnings, with ARR exceeding $2.8B driven by enterprise adoption of their unified security monitoring stack.",
  "Shopify": "Shopify is a commerce platform that enables businesses of all sizes to sell online, in-store, and everywhere in between. Key products include Shopify Plus for enterprise, Shop Pay, and their expanding B2B wholesale channel. Recent developments include their acquisition of a logistics automation startup in January 2026 and the launch of Shopify Magic — an AI assistant for merchants that generates product descriptions, marketing copy, and store analytics summaries.",
  "HubSpot": "HubSpot is an all-in-one CRM and marketing automation platform serving mid-market and enterprise companies. Their product ecosystem spans Marketing Hub, Sales Hub, Service Hub, and the recently launched Operations Hub. In Q1 2026, HubSpot announced a strategic partnership with Snowflake for deeper data integration and rolled out AI-powered lead scoring that has seen rapid adoption among their enterprise customer base.",
  "Canva": "Canva is a visual communication platform used by over 190 million people worldwide for design, presentations, and content creation. Their product line includes Canva for Teams, Canva Docs, and Magic Studio — their generative AI design suite. Recently, Canva completed its IPO preparations and launched enterprise-grade brand management tools with SSO, advanced permissions, and compliance features targeting Fortune 500 companies.",
  "Figma": "Figma is a collaborative interface design platform widely used for UI/UX design, prototyping, and design systems management. Key products include Figma Design, FigJam (whiteboarding), and Dev Mode for developer handoff. In early 2026, Figma launched AI-powered auto-layout suggestions and a new enterprise admin console, while also expanding its plugin ecosystem to over 5,000 community-built integrations.",
  "Plaid": "Plaid is a fintech infrastructure company that connects applications to users' bank accounts, enabling seamless financial data access. Their products include Auth, Transactions, Identity, and the newer Income verification APIs. Recent news includes Plaid's acquisition of a small payments orchestration startup and their expansion into European markets with new open banking partnerships, positioning them as a key player in the embedded finance ecosystem.",
  "Notion": "Notion is a connected workspace platform that combines notes, docs, wikis, project management, and databases into a single tool. Their enterprise offering includes advanced permissions, SAML SSO, and audit logs. Recently, Notion launched Notion AI — a built-in assistant for summarizing meeting notes, generating action items, and drafting documents — and announced a major partnership with Slack for deeper workspace integration.",
};

interface BriefSection { key: string; paragraphs: string[] }
interface BriefCompany { description?: string; updates?: string[] }

// Normalize a domain for matching brief-company entries to live accounts.
// Strips protocol, leading www., and any path so "https://www.acme.com/x" and
// "acme.com" compare equal.
function normalizeBriefDomain(domain: string | null | undefined): string {
  if (!domain) return '';
  return domain.toLowerCase().trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '');
}

/** Brief sections with linked names — read-only with LinkedText */
function BackgroundSection({
  useBackendData, aiBrief, briefSections, attendeeDetails, workspaceName,
  emailSummaries, meetingTranscripts, onPersonClick, onCompanyClick, onMeetingClick,
  onEngagementClick,
}: {
  useBackendData: boolean;
  aiBrief: string;
  briefSections: BriefSection[];
  attendeeDetails: { name: string; person: any; company: any }[];
  workspaceName: string | undefined;
  emailSummaries: Record<string, string>;
  meetingTranscripts: Record<string, string>;
  onPersonClick: (person: any) => void;
  onCompanyClick: (company: any) => void;
  onMeetingClick: (meeting: any) => void;
  onEngagementClick?: (engagementId: string, label: string) => void;
}) {
  const sections = useMemo((): BriefSection[] => {
    if (useBackendData && aiBrief && briefSections.length > 0) return briefSections;

    const externalAttendees = attendeeDetails.filter(a => a.person && a.person.company !== workspaceName);
    const primaryAttendee = externalAttendees[0];
    const companyName = primaryAttendee?.person?.company || "the client";

    return [{ key: "background", paragraphs: [
      `INTERACTION HISTORY\nWe've had ${externalAttendees.length > 1 ? "multiple touchpoints" : "several interactions"} with ${companyName} over the past few months. ${primaryAttendee ? emailSummaries[primaryAttendee.name] || "" : ""}${externalAttendees.length > 1 && emailSummaries[externalAttendees[1]?.name] ? ` Additionally, ${externalAttendees[1].name} has been involved in parallel threads. ${emailSummaries[externalAttendees[1].name]}` : ""}`,
      `KEY POINTS\n${primaryAttendee && meetingTranscripts[primaryAttendee.name] ? meetingTranscripts[primaryAttendee.name] : "Previous discussions have centered around requirements gathering and initial scoping for the engagement."}`,
      `RELATIONSHIP STRENGTH\nThe relationship is progressing well with strong engagement from their team on key decision points. ${externalAttendees.length > 1 ? `Multiple stakeholders (${externalAttendees.map(a => a.name).join(", ")}) are actively involved, which signals organizational buy-in.` : "The primary contact has been responsive and engaged throughout the process."}`,
      `NEXT STEPS\n• Send updated proposal incorporating feedback from last discussion\n• Schedule follow-up review for next sprint\n• Confirm technical requirements with engineering team`,
    ]}];
  }, [useBackendData, aiBrief, briefSections, attendeeDetails, workspaceName, emailSummaries, meetingTranscripts]);

  // Production links only this meeting's real attendees and their accounts;
  // undefined lets LinkedText use demo names in preview.
  const linkedEntities = useMemo((): LinkedEntity[] | undefined => {
    if (!useBackendData) return undefined;
    const list: LinkedEntity[] = [];
    for (const a of attendeeDetails) {
      if (a.person?.id) list.push({ name: a.person.name, type: "person", data: a.person });
      if (a.company?.id) list.push({ name: a.company.name, type: "company", data: a.company });
    }
    return list;
  }, [useBackendData, attendeeDetails]);

  const formatHeading = (key: string) =>
    key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');

  // Parse [text](engagement:id) markdown emitted by the brief LLM, splitting the
  // paragraph into plain-text segments (still passed to LinkedText for name/URL
  // detection) and engagement-link buttons. Falls back gracefully if no markdown
  // links are present.
  const renderParagraph = (text: string, paragraphKey: string) => {
    const regex = /\[([^\]]+)\]\(engagement:([^)]+)\)/g;
    const parts: Array<{ kind: "text" | "link"; content: string; engagementId?: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ kind: "text", content: text.slice(lastIndex, match.index) });
      }
      parts.push({ kind: "link", content: match[1], engagementId: match[2] });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push({ kind: "text", content: text.slice(lastIndex) });
    }
    if (parts.length === 0) {
      parts.push({ kind: "text", content: text });
    }

    return parts.map((part, i) => {
      if (part.kind === "link" && part.engagementId && onEngagementClick) {
        return (
          <button
            key={`${paragraphKey}-${i}`}
            onClick={(e) => { e.stopPropagation(); onEngagementClick(part.engagementId!, part.content); }}
            className="font-medium text-foreground underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground/60 transition cursor-pointer"
          >
            {part.content}
          </button>
        );
      }
      return (
        <LinkedText
          key={`${paragraphKey}-${i}`}
          text={part.content}
          entities={linkedEntities}
          onPersonClick={onPersonClick}
          onCompanyClick={onCompanyClick}
          onMeetingClick={onMeetingClick}
        />
      );
    });
  };

  return (
    <>
      {sections.map((section) => (
        <section key={section.key}>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{formatHeading(section.key)}</h4>
          <div className="space-y-4">
            {section.paragraphs.map((paragraph, pIdx) => {
              // Split off a leading ALL-CAPS heading line so it can be styled
              // like the company/person titles below, with the body text below
              // matched to the company description styling.
              const newlineIdx = paragraph.indexOf("\n");
              let heading: string | null = null;
              let body = paragraph;
              if (newlineIdx > 0) {
                const firstLine = paragraph.slice(0, newlineIdx).trim();
                const rest = paragraph.slice(newlineIdx + 1);
                if (firstLine && firstLine === firstLine.toUpperCase() && /[A-Z]/.test(firstLine)) {
                  heading = firstLine
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase());
                  body = rest;
                }
              }
              return (
                <div key={pIdx} className="space-y-1.5">
                  {heading && (
                    <p className="text-sm font-semibold text-foreground">{heading}</p>
                  )}
                  <p className="text-muted-foreground text-xs leading-relaxed whitespace-pre-line">
                    {renderParagraph(body, `${section.key}-${pIdx}`)}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}

export default function MeetingBriefingSheet({ open, onOpenChange, meeting, isRecording: isRecordingProp, onRecordingChange, onRecordingSync, hideOverlay = false, onBack }: Props) {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const { currentUser } = useCurrentUser();
  const workspaceDomain = currentUser?.workspaces?.find(ws => ws.id === workspaceId)?.domain;
  const workspaceName = currentUser?.workspaces?.find(ws => ws.id === workspaceId)?.name;
  const isRecentChangesEnabled = useFeatureFlag(FEATURE_FLAGS.BRIEFING_RECENT_CHANGES);
  const [qaMessages, setQaMessages] = useState<QAMessage[]>([]);
  const [askInput, setAskInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [askCoffeePanelOpen, setAskCoffeePanelOpen] = useState(false);
  const [askCoffeeQuery, setAskCoffeeQuery] = useState("");
  const [localRecording, setLocalRecording] = useState(false);
  const isRecording = isRecordingProp ?? localRecording;
  const scrollEndRef = useRef<HTMLDivElement>(null);
  const [navStack, setNavStack] = useState<NavigationItem[]>([]);
  const currentNav = navStack.length > 0 ? navStack[navStack.length - 1] : null;
  const pushNav = useCallback((item: NavigationItem) => setNavStack(prev => [...prev, item]), []);
  const popNav = useCallback(() => setNavStack(prev => prev.slice(0, -1)), []);

  // Re-render when person edits change anywhere so attendee details stay in sync.
  const [, setPersonEditTick] = useState(0);
  useEffect(() => personEditStore.subscribe(() => setPersonEditTick(t => t + 1)), []);

  // PersonDetailSheet state (stacked sheet)
  const [personSheetOpen, setPersonSheetOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<typeof people[0] | null>(null);

  // CompanyDetailSheet state (stacked sheet)
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<typeof companies[0] | null>(null);

  // MeetingIntelligenceSheet state (stacked sheet for clicking meeting references)
  const [linkedMeetingOpen, setLinkedMeetingOpen] = useState(false);
  const [linkedMeeting, setLinkedMeeting] = useState<Meeting | null>(null);

  // Helper to navigate to a person
  const handlePersonClick = useCallback((person: typeof people[0]) => {
    setSelectedPerson(person);
    setPersonSheetOpen(true);
  }, []);

  // Helper to navigate to a company
  const handleCompanyClick = useCallback((company: any) => {
    console.log('[MeetingBriefingSheet] Opening company:', company);
    setSelectedCompany(company);
    setCompanySheetOpen(true);
  }, []);

  // Helper to navigate to a linked meeting
  const handleMeetingClick = useCallback((meetingData: any) => {
    setLinkedMeeting({
      id: meetingData.id,
      title: meetingData.title,
      date: meetingData.date,
      time: meetingData.time,
      duration: meetingData.duration,
      type: meetingData.type,
      attendees: meetingData.attendees || [],
      link: meetingData.link || meetingData.meetingUrl,
    });
    setLinkedMeetingOpen(true);
  }, []);

  // Briefing templates from settings
  type BriefingTemplate = { id: string; name: string; type: "briefing" | "summary"; sections: Array<{ title: string; prompt: string; fieldMapping: string }>; builtIn?: boolean };
  // "Customer Success" is a preview-only demo template. SettingsPage owns the
  // canonical seed; outside preview we seed nothing and filter these ids out so
  // they can never leak into the production briefing dropdown — even if an older
  // build already persisted them to localStorage.
  const PREVIEW_ONLY_TEMPLATE_IDS = ["customer-success-briefing", "customer-success-summary"];
  const PREVIEW_DEFAULT_TEMPLATES: BriefingTemplate[] = [
    { id: "customer-success-briefing", name: "Customer Success", type: "briefing", sections: [
      { title: "Account Health", prompt: "Summarize current health signals — recent usage, NPS, open support tickets, and any recent escalations.", fieldMapping: "none" },
      { title: "Renewal & Expansion", prompt: "Current contract value, renewal date, and any expansion opportunities discussed previously.", fieldMapping: "none" },
      { title: "Recent Activity", prompt: "Most recent meetings, emails, and notable customer touchpoints in the past 30 days.", fieldMapping: "none" },
    ]},
    { id: "customer-success-summary", name: "Customer Success", type: "summary", sections: [
      { title: "Customer Sentiment", prompt: "Overall sentiment expressed by the customer during the meeting.", fieldMapping: "none" },
      { title: "Adoption & Usage", prompt: "Adoption signals and product usage discussed in the meeting.", fieldMapping: "none" },
      { title: "Risks & Blockers", prompt: "Risks, blockers, or concerns raised by the customer.", fieldMapping: "none" },
      { title: "Expansion Opportunities", prompt: "Cross-sell or upsell opportunities discussed.", fieldMapping: "none" },
      { title: "Next Steps", prompt: "Agreed-upon next steps and owners.", fieldMapping: "none" },
    ]},
  ];
  const [savedTemplates] = useStickyState<BriefingTemplate[]>(
    "settings:custom-templates",
    isLovablePreview() ? PREVIEW_DEFAULT_TEMPLATES : [],
  );
  // Only show the template selector when the user has created custom briefing
  // templates. Built-in templates (e.g. "General") are hidden so the menu
  // doesn't appear redundant alongside the Default Briefing.
  const briefingTemplates = useMemo(
    () => savedTemplates.filter(t =>
      t.type === "briefing" && !t.builtIn &&
      (isLovablePreview() || !PREVIEW_ONLY_TEMPLATE_IDS.includes(t.id))
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedTemplates]
  );
  const [selectedTemplateId, setSelectedTemplateId] = useStickyState<string>("meeting:selected-briefing-template", "default");

  // If the persisted template selection no longer exists (e.g. a stray
  // preview-only template was removed in production), fall back to the default
  // briefing so the sheet never renders blank.
  useEffect(() => {
    if (selectedTemplateId !== "default" && !briefingTemplates.some(t => t.id === selectedTemplateId)) {
      setSelectedTemplateId("default");
    }
  }, [selectedTemplateId, briefingTemplates, setSelectedTemplateId]);

  // Backend data hooks
  const [getEngagementBrief, { data: engagementData, loading: engagementLoading, error: engagementError }] = useGetEngagementBriefLazyQuery({
    fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache for stale meetings
  });
  const [updateMeetingRecording] = useUpdateMeetingRecordingMutation();

  // Fetch engagement details when sheet opens
  const hasSyncedRecording = useRef(false);
  useEffect(() => {
    if (open && meeting?.id && workspaceId && !isLovablePreview()) {
      hasSyncedRecording.current = false; // Reset for new sheet open
      getEngagementBrief({
        variables: {
          id: meeting.id,
          workspaceId: String(workspaceId),
        },
      });
    }
  }, [open, meeting?.id, workspaceId, getEngagementBrief]);

  // Initialize chat agent for Ask AI feature
  const chatContext: ChatContext | undefined = useMemo(() =>
    !isLovablePreview() && meeting?.id
      ? {
        type: 'meeting',
        id: meeting.id,
      }
      : undefined,
    [meeting?.id]
  );

  const { state: chatState, actions: chatActions } = useChatAgent({
    initialContext: chatContext,
    initializeSession: open && !!chatContext, // Only create session when sheet is actually open
  });

  // Reset chat when sheet closes
  useEffect(() => {
    if (!open) {
      chatActions.reset();
    }
  }, [open, chatActions]);

  // Read-only users can toggle recording only with the `read-only-meetings` flag.
  const canUseMeetingCapture = useCanUseMeetingCapture();

  // Handle recording toggle
  const toggleRecording = async () => {
    if (!canUseMeetingCapture) return;
    const next = !isRecording;

    // Update local/parent state immediately
    if (onRecordingChange) {
      // Parent handles both state and backend persistence
      onRecordingChange(next);
      return;
    }

    await sendRecording(next);
  };

  // Standalone mode only. RECORDING_EXISTS means nothing changed: ask, then resend with override.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const sendRecording = async (next: boolean, override = false) => {
    setLocalRecording(next);

    // Call backend mutation if not in preview mode
    if (!isLovablePreview() && meeting?.id && workspaceId) {
      try {
        const response = await updateMeetingRecording({
          variables: {
            workspaceId: Number(workspaceId),
            id: meeting.id,
            enabled: next,
            override,
          },
        });
        if (response.data?.updateMeetingRecording?.code === RECORDING_EXISTS) {
          setLocalRecording(!next);
          setOverrideOpen(true);
        }
      } catch (error) {
        console.error('Failed to update meeting recording:', error);
        setLocalRecording(!next);
      }
    }
  };

  // Use backend data when available, fallback to dummy data in preview mode
  // IMPORTANT: Verify the cached engagement ID matches the current meeting ID to prevent showing stale data
  const engagement = engagementData?.engagement;
  const isCorrectEngagement = engagement?.id === meeting?.id;
  const useBackendData = !isLovablePreview() && engagement && isCorrectEngagement;

  // Sync recording state from backend on initial engagement load only.
  // Uses onRecordingSync (silent, no toast/mutation) so the parent can update its state
  // without triggering the "Recording Enabled" toast or a redundant backend call.
  // Only syncs once per sheet open — after that, local state is authoritative.
  useEffect(() => {
    if (!engagement || !isCorrectEngagement || hasSyncedRecording.current) return;
    hasSyncedRecording.current = true;
    const backendRecordingState = engagement.recordingEnabled ?? engagement.hasActiveBot ?? false;
    if (backendRecordingState !== isRecording) {
      (onRecordingSync ?? onRecordingChange)?.(backendRecordingState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagement?.recordingEnabled, engagement?.hasActiveBot, isCorrectEngagement]);

  // Extract attendees from backend or dummy data
  const backendContacts = engagement?.contacts || [];

  const attendeeDetailsRaw = !meeting ? [] : useBackendData
    ? backendContacts.map(contact => {
      const displayName = getDisplayName(contact.properties?.firstName, contact.properties?.lastName, contact.primaryEmail);

      const account = contact.accounts?.edges?.[0]?.node;

      return {
        name: displayName,
        person: {
          id: contact.id,
          name: displayName,
          email: contact.primaryEmail || '',
          title: contact.properties?.jobTitle || '',
          company: account?.properties?.name || '',
          location: [contact.properties?.city, contact.properties?.state].filter(Boolean).join(', '),
          linkedin: contact.properties?.linkedinProfileUrl || '',
          avatar: contact.profileImageUrl || '',
        },
        company: account ? {
          id: account.id,
          name: account.properties?.name || '',
          domain: account.domain || '',
          industry: '', // Not available in this query
          employees: '', // Not available in this query
          revenue: '', // Not available in this query
          description: '', // Not available in this query
          location: '', // Not available in this query
          website: account.domain || '',
          logo: account.properties?.logoUrl || '',
        } : null,
      };
    })
    : (meeting.attendees || []).map(name => {
      const basePerson = people.find(p => p.name === name);
      // Merge any in-session edits from the shared person edit store so
      // briefings reflect updated name/title/email/etc. immediately.
      const person = basePerson ? personEditStore.apply(basePerson) : basePerson;
      // Fallback for internal workspace members not in the people (contacts) array
      if (!person) {
        const internalMember = internalTeamDetails[name];
        if (internalMember) {
          return { name, person: internalMember, company: companies.find(c => c.name === "Gazillion") || null };
        }
      }
      const company = person ? companies.find(c => c.name === person.company) : null;
      // If the person's display name was edited, surface the new name in the
      // attendee label so briefings reflect the rename consistently.
      const displayName = person?.name || name;
      return { name: displayName, person, company };
    });

  // Display-only fallback: group an account-less attendee under a company another attendee
  // in this meeting is linked to by matching work-email domain (skip personal/free domains).
  const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'live.com',
    'icloud.com', 'me.com', 'aol.com', 'proton.me', 'protonmail.com', 'msn.com',
  ]);
  const readDomain = (c: { domain?: string; website?: string } | null | undefined): string | undefined => {
    if (!c) return undefined;
    if (c.domain) return c.domain.toLowerCase();
    // Some company records carry only a website (e.g. "stripe.com" or "https://stripe.com").
    if (c.website) return c.website.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || undefined;
    return undefined;
  };
  const domainToCompany = new Map<string, NonNullable<(typeof attendeeDetailsRaw)[number]['company']>>();
  for (const a of attendeeDetailsRaw) {
    const dom = readDomain(a.company);
    if (dom && a.company) domainToCompany.set(dom, a.company);
  }
  const attendeeDetails = domainToCompany.size === 0 ? attendeeDetailsRaw : attendeeDetailsRaw.map(a => {
    if (a.company || !a.person) return a;
    const dom = a.person.email?.split('@')[1]?.toLowerCase();
    if (!dom || FREE_EMAIL_DOMAINS.has(dom)) return a;
    const match = domainToCompany.get(dom);
    if (!match) return a;
    return { ...a, person: { ...a.person, company: match.name }, company: match as typeof a.company };
  });

  // Deduplicate companies
  const uniqueCompanies = Array.from(
    new Map(attendeeDetails.filter(a => a.company).map(a => [a.company!.id, a.company!])).values()
  );

  const attendeeNames = attendeeDetails.map(a => a.name);

  // Parse AI brief JSON and convert to BlockNote blocks
  const aiBrief = engagement?.aiBrief || '';
  const briefBlocks: PartialBlock[] = useMemo(() => {
    if (!aiBrief) {
      return [{
        type: "paragraph" as const,
        content: [{
          type: "text" as const,
          text: "No briefing available yet. AI will generate a briefing closer to the meeting time.",
          styles: { italic: true }
        }]
      }];
    }

    try {
      // Strip markdown code fences (```json ... ```) that backend sometimes wraps around the JSON
      let rawBrief = aiBrief.trim();
      if (rawBrief.startsWith('```')) {
        rawBrief = rawBrief.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?\s*```\s*$/, '');
      }
      const briefData = JSON.parse(rawBrief);

      // Use the reference implementation's briefToBlocks function
      const blocks = briefToBlocks(briefData, t);

      return blocks.length > 0 ? blocks : [{
        type: "paragraph" as const,
        content: [{
          type: "text" as const,
          text: "No briefing content available.",
          styles: { italic: true }
        }]
      }];
    } catch (error) {
      console.error('Failed to parse AI brief JSON:', error);
      // Fallback to plain text display if aiBrief has content
      if (aiBrief && aiBrief.trim()) {
        return aiBrief.split('\n\n').filter(Boolean).map(paragraph => ({
          type: "paragraph" as const,
          content: [{
            type: "text" as const,
            text: paragraph,
            styles: {},
          }]
        }));
      }
      // If empty or parsing failed, show empty state
      return [{
        type: "paragraph" as const,
        content: [{
          type: "text" as const,
          text: "No briefing content available.",
          styles: { italic: true }
        }]
      }];
    }
    // `t` is a dependency: the section headings come from the catalog, so the
    // blocks have to be rebuilt when the user switches language.
  }, [aiBrief, t]);

  // Parse the Companies block out of the brief JSON. briefSections drops
  // `companies` on purpose (it's a structured object, not paragraph content),
  // so the rich description + updates the brief generated were never rendered.
  // We index by both name and domain so the enrichment survives a company
  // rename: the brief JSON is a snapshot keyed by the name/domain at generation
  // time, so joining on name alone loses the data the moment a user edits the
  // name (e.g. removes a trailing comma).
  const briefCompanies = useMemo<{ byName: Record<string, BriefCompany>; byDomain: Record<string, BriefCompany> }>(() => {
    const empty = { byName: {}, byDomain: {} };
    if (!aiBrief) return empty;
    try {
      let rawJson = aiBrief.trim();
      if (rawJson.startsWith('```')) {
        rawJson = rawJson.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?\s*```\s*$/, '');
      }
      const briefData = JSON.parse(rawJson);
      const companies = briefData?.Companies;
      if (!Array.isArray(companies)) return empty;
      const byName: Record<string, BriefCompany> = {};
      const byDomain: Record<string, BriefCompany> = {};
      companies.forEach((c: any) => {
        if (!c || typeof c.name !== 'string' || !c.name.trim()) return;
        const entry: BriefCompany = {
          description: typeof c.description === 'string' && c.description.trim() ? c.description.trim() : undefined,
          updates: Array.isArray(c.updates)
            ? c.updates.filter((u: any) => typeof u === 'string' && u.trim()).map((u: string) => u.trim())
            : undefined,
        };
        byName[c.name.toLowerCase().trim()] = entry;
        const nd = normalizeBriefDomain(c.domain);
        if (nd) byDomain[nd] = entry;
      });
      return { byName, byDomain };
    } catch {
      return empty;
    }
  }, [aiBrief]);

  // Resolve a company's brief enrichment by domain first (stable across
  // renames), then fall back to name.
  const lookupBriefCompany = useCallback((c: { name: string; domain?: string | null; website?: string | null }) => {
    const nd = normalizeBriefDomain(('domain' in c ? c.domain : undefined) ?? c.website);
    if (nd && briefCompanies.byDomain[nd]) return briefCompanies.byDomain[nd];
    return briefCompanies.byName[c.name.toLowerCase().trim()];
  }, [briefCompanies]);

  const briefSections = useMemo((): BriefSection[] => {
    const skipKeys = new Set(['attendees', 'companies', 'engagementhistory', 'lastactivity', 'lastactivityid', 'nextstep', 'nextstepid']);
    if (aiBrief) {
      try {
        let rawJson = aiBrief.trim();
        if (rawJson.startsWith('```')) {
          rawJson = rawJson.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?\s*```\s*$/, '');
        }
        const briefData = JSON.parse(rawJson);
        const sections: BriefSection[] = [];
        for (const [key, value] of Object.entries(briefData)) {
          if (skipKeys.has(key.toLowerCase())) continue;
          const paragraphs: string[] = [];
          if (Array.isArray(value)) {
            value.forEach((item: unknown) => {
              if (typeof item === 'string' && item.trim()) paragraphs.push(item.trim());
            });
          } else if (typeof value === 'string' && value.trim()) {
            paragraphs.push(value.trim());
          }
          if (paragraphs.length > 0) sections.push({ key, paragraphs });
        }
        if (sections.length > 0) return sections;
      } catch {
        // Fall through to block-based extraction
      }
    }
    // Fallback: extract from briefBlocks as a single "background" section
    const paragraphs = briefBlocks
      .map((block) => {
        const content = (block as any)?.content;
        if (typeof content === "string") return content;
        if (Array.isArray(content)) {
          return content
            .map((item: any) => (typeof item === "string" ? item : item?.text || ""))
            .join("");
        }
        return "";
      })
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
    return paragraphs.length > 0 ? [{ key: "background", paragraphs }] : [];
  }, [briefBlocks, aiBrief]);

  const handleAsk = async () => {
    const q = askInput.trim();
    if (!q) return;
    setAskInput("");
    // Enrich the query with the meeting context so dummy/AI lookups can resolve
    // company- or attendee-specific questions even when the user phrases them
    // generically ("any new info on the company?").
    const contextBits = [
      meeting?.title,
      attendeeDetails
        .map(a => a.company?.name)
        .filter((v, i, arr): v is string => !!v && arr.indexOf(v) === i)
        .join(", "),
    ]
      .filter(Boolean)
      .join(" · ");
    const enrichedQuery = contextBits ? `${q} (context: ${contextBits})` : q;
    setAskCoffeeQuery(enrichedQuery);
    // Defer opening to next tick to ensure query state is updated first
    setTimeout(() => setAskCoffeePanelOpen(true), 0);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) setNavStack([]); onOpenChange(o); }}>
        <SheetContent expandable hideOverlay={hideOverlay} className="sm:max-w-[50vw] w-full p-0 flex flex-col">
          {!meeting ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground text-xs">{t("meetings.noMeetingSelected")}</p>
            </div>
          ) : (
            <>
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                <div>
                  {onBack && (
                    <button onClick={onBack} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-3 transition">
                      <ArrowLeft className="h-4 w-4" /> {t("meetings.back")}
                    </button>
                  )}
                  <div className="flex items-center justify-between">
                    <SheetTitle className="text-lg font-display">{t("detailSheets.meetingBriefing")}</SheetTitle>
                  </div>
                  <p className="text-muted-foreground text-xs">{meeting.title}</p>
                  <div className="flex items-end justify-between">
                    <p className="text-xs text-muted-foreground">{meeting.date} · {meeting.time} · {meeting.duration}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <MutationGate allowed={canUseMeetingCapture} reason={READ_ONLY_MEETINGS_REASON}>
                        <button
                          onClick={toggleRecording}
                          disabled={!canUseMeetingCapture}
                          className="text-xs px-3 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-secondary flex items-center gap-1.5 transition"
                        >
                          {isRecording && <Check className="h-3 w-3 text-accent shrink-0" />}
                          {t("detailSheets.record")}
                        </button>
                      </MutationGate>
                      {meeting.link && (
                        <button
                          onClick={() => window.open(meeting.link, "_blank", "noopener,noreferrer")}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-accent text-accent-foreground hover:opacity-90 transition"
                        >
                          <Video className="h-3 w-3" />
                          {t("detailSheets.join")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <EngagementPrivacyGate engagementType="meeting" engagement={engagement} loading={engagementLoading && !isLovablePreview()} error={engagementError}>
              {/* Search bar - always on top */}
              <div className="px-6 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      value={askInput}
                      onChange={e => setAskInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleAsk()}
                      placeholder={t("detailSheets.askAboutMeeting")}
                      className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20"
                    />
                  </div>
                  <button
                    onClick={handleAsk}
                    disabled={!askInput.trim()}
                    className="shrink-0 p-2.5 rounded-lg bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {(() => {
                // Shared Attendees + Companies sections rendered above every
                // template (including custom ones) so switching templates only
                // swaps the body content below, not the header context.
                const isInternalPerson = (person: typeof people[0] | undefined): boolean => {
                  if (!person?.email || !workspaceDomain) return false;
                  const emailDomain = person.email.split('@')[1]?.toLowerCase();
                  return emailDomain === workspaceDomain.toLowerCase();
                };
                const grouped = new Map<string, typeof attendeeDetails>();
                const internalGroup: typeof attendeeDetails = [];
                attendeeDetails.forEach(a => {
                  if (!a.person || isInternalPerson(a.person)) {
                    internalGroup.push(a);
                  } else {
                    const key = a.person.company || t("meetings.unknownCompany");
                    if (!grouped.has(key)) grouped.set(key, []);
                    grouped.get(key)!.push(a);
                  }
                });
                const renderAttendee = ({ name, person }: typeof attendeeDetails[0]) => {
                  const linkedinRaw = person?.linkedin || null;
                  const linkedinSlug = linkedinRaw
                    ? linkedinRaw.replace(/^https?:\/\//, '').replace(/^(www\.)?linkedin\.com\/(in|company)\//, '').replace(/\/+$/, '').split('/').pop() || null
                    : null;
                  const linkedinHref = linkedinRaw
                    ? (linkedinRaw.startsWith('http') ? linkedinRaw : `https://${linkedinRaw}`)
                    : null;
                  return (
                    <li key={name} className="text-sm text-foreground py-0.5 list-disc ml-4">
                      <div>
                        <span className="inline-flex items-center flex-wrap gap-x-1.5">
                          <button onClick={() => person && handlePersonClick(person as any)} className="font-medium hover:underline cursor-pointer">{name}</button>
                          {person?.title && <span className="text-muted-foreground">— {person.title}</span>}
                        </span>
                        {(person?.location || linkedinSlug || person?.email) && (
                          <div className="ml-4 mt-1 mb-0.5 pl-2 border-l-2 border-muted/40 flex flex-col gap-0.5 text-xs text-muted-foreground">
                            {person?.location && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3 shrink-0" />{person.location}
                              </span>
                            )}
                            {linkedinSlug && linkedinHref && (
                              <a href={linkedinHref} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-accent hover:underline">
                                <LinkedInIcon className="h-3 w-3 shrink-0" />/{linkedinSlug}
                              </a>
                            )}
                            {person?.email && (
                              <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1 text-accent hover:underline">
                                <Mail className="h-3 w-3 shrink-0" />{person.email}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                };
                const attendeesSection = (
                  <section>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("meetings.attendees")}</h4>
                    <div className="space-y-3">
                      {Array.from(grouped.entries()).map(([companyName, members]) => {
                        const companyDomain = members[0]?.company ? (('domain' in members[0].company ? (members[0].company as any).domain : (members[0].company as any).website) ?? null) : null;
                        return (
                          <div key={companyName}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <CompanyLogo name={companyName} domain={companyDomain} size="sm" />
                              {members[0]?.company ? (
                                <button onClick={() => handleCompanyClick(members[0].company)} className="text-sm font-semibold text-foreground hover:underline cursor-pointer">{companyName}</button>
                              ) : (
                                <p className="text-sm font-semibold text-foreground">{companyName}</p>
                              )}
                            </div>
                            <ul className="space-y-1 pl-1">
                              {members.map(renderAttendee)}
                            </ul>
                          </div>
                        );
                      })}
                      {internalGroup.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1">
                            <CompanyLogo name={internalGroup.find(a => a.person?.company)?.person?.company || workspaceName || t("meetings.internalTeam")} domain={workspaceDomain || null} size="sm" />
                            <p className="text-sm font-semibold text-foreground">{internalGroup.find(a => a.person?.company)?.person?.company || workspaceName || t("meetings.internalTeam")}</p>
                          </div>
                          <ul className="space-y-1 pl-1">
                            {internalGroup.map(renderAttendee)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </section>
                );
                const hasBriefCompanies = Object.keys(briefCompanies.byName).length > 0;
                const externalCompanies = uniqueCompanies.filter(c => {
                  if (hasBriefCompanies) return !!lookupBriefCompany(c);
                  const domain = 'domain' in c ? (c as any).domain : (c as any).website;
                  if (!domain || !workspaceDomain) return true;
                  return domain.toLowerCase() !== workspaceDomain.toLowerCase();
                });
                const companiesSection = externalCompanies.length === 0 ? null : (
                  <section>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("meetings.companies")}</h4>
                    <div className="space-y-4">
                      {externalCompanies.map(c => {
                        const briefCompany = lookupBriefCompany(c);
                        const description = briefCompany?.description
                          || c.description
                          || t("meetings.companyDescriptionFallback", { name: c.name, industry: c.industry || t("meetings.industryFallback") });
                        const updates = briefCompany?.updates || [];
                        return (
                          <div key={c.id} className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <CompanyLogo name={c.name} domain={('domain' in c ? (c as any).domain : (c as any).website) ?? null} size="sm" />
                              <button onClick={() => handleCompanyClick(c)} className="text-sm font-semibold text-foreground hover:underline cursor-pointer">{c.name}</button>
                            </div>
                            <div className="pl-1 space-y-2">
                              {companyIntelligence[c.name] ? (
                                <p className="text-muted-foreground text-xs leading-relaxed">
                                  <Sparkles className="h-3 w-3 inline-block mr-1 text-accent shrink-0 -mt-0.5" />
                                  {companyIntelligence[c.name]}
                                </p>
                              ) : (
                                <p className="text-muted-foreground text-xs leading-relaxed">
                                  <Sparkles className="h-3 w-3 inline-block mr-1 text-accent shrink-0 -mt-0.5" />
                                  {description}
                                  {c.website && !briefCompany?.description && (
                                    <> {t("meetings.learnMoreAt")} <a href={`https://${c.website}`} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{c.website}</a>.</>
                                  )}
                                </p>
                              )}
                              {updates.length > 0 && (
                                <div className="space-y-1">
                                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t("meetings.recentUpdates")}</p>
                                  <ul className="space-y-1">
                                    {updates.map((u, i) => (
                                      <li key={i} className="text-muted-foreground text-xs leading-relaxed flex items-start gap-1.5">
                                        <TrendingUp className="h-3 w-3 inline-block mt-1 text-accent shrink-0" />
                                        <span>{u}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
                return (
                  <>
                    {/* Custom template view */}
                    {selectedTemplateId !== "default" && (() => {
                const tpl = briefingTemplates.find(t => t.id === selectedTemplateId);
                if (!tpl) return null;

                // Use real AI brief if available, otherwise use dummy content
                const aiBrief = engagement?.aiBrief;
                const hasRealBrief = useBackendData && aiBrief;

                const dummyContent = previewBriefingContent(meeting.attendees);

                return (
                  <ScrollArea className="flex-1 px-6 py-4">
                    {engagementLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : hasRealBrief ? (
                      <div className="space-y-6 pb-6">
                        {attendeesSection}
                        {companiesSection}
                        {briefSections.length > 0 ? briefSections.map((section, i) => (
                          <section key={section.key || i}>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              {humanizeSectionKey(section.key)}
                            </h4>
                            <div className="pl-1 space-y-2">
                              {section.paragraphs.map((p, j) => (
                                <p key={j} className="text-muted-foreground text-xs leading-relaxed whitespace-pre-line">{p}</p>
                              ))}
                            </div>
                          </section>
                        )) : (
                          <p className="text-sm text-muted-foreground">{t("meetings.noBriefingContent")}</p>
                        )}
                        {briefingTemplates.length > 0 && (
                          <section className="pt-4 border-t border-border">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("meetings.briefingTemplate")}</h4>
                            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                              <SelectTrigger className="w-full h-9 text-sm">
                                <SelectValue placeholder={t("meetings.templatePlaceholder")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">{t("meetings.defaultBriefing")}</SelectItem>
                                {briefingTemplates.map(t => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </section>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-6 pb-6">
                        {attendeesSection}
                        {companiesSection}
                        {tpl.sections.map((section, i) => (
                          <section key={i}>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{section.title}</h4>
                            <div className="pl-1 space-y-2">
                              <p className="text-muted-foreground text-xs leading-relaxed whitespace-pre-line">
                                {dummyContent[section.title] || `AI-generated briefing based on: "${section.prompt}"`}
                              </p>
                            </div>
                          </section>
                        ))}

                        {/* Template selector */}
                        {briefingTemplates.length > 0 && (
                          <section className="pt-4 border-t border-border">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("meetings.briefingTemplate")}</h4>
                            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                              <SelectTrigger className="w-full h-9 text-sm">
                                <SelectValue placeholder={t("meetings.templatePlaceholder")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">{t("meetings.defaultBriefing")}</SelectItem>
                                {briefingTemplates.map(t => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </section>
                        )}
                      </div>
                    )}
                  </ScrollArea>
                );
              })()}

              {/* Default briefing view - single scrollable layout like summary */}
              {selectedTemplateId === "default" && (
                <>
                  {engagementLoading && !isLovablePreview() ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <ScrollArea className="flex-1 px-6 py-4">
                      <div className="space-y-6 pb-6">

                        {/* ── Attendees (grouped by company, external first) ── */}
                        <section>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("meetings.attendees")}</h4>
                          {(() => {
                            const grouped = new Map<string, typeof attendeeDetails>();
                            const internalGroup: typeof attendeeDetails = [];

                            const isInternalPerson = (person: typeof people[0] | undefined): boolean => {
                              if (!person?.email || !workspaceDomain) return false;
                              const emailDomain = person.email.split('@')[1]?.toLowerCase();
                              return emailDomain === workspaceDomain.toLowerCase();
                            };

                            attendeeDetails.forEach(a => {
                              if (!a.person || isInternalPerson(a.person)) {
                                internalGroup.push(a);
                              } else {
                                const key = a.person.company || t("meetings.unknownCompany");
                                if (!grouped.has(key)) grouped.set(key, []);
                                grouped.get(key)!.push(a);
                              }
                            });

                            const renderAttendee = ({ name, person }: typeof attendeeDetails[0]) => {
                              const linkedinRaw = person?.linkedin || null;
                              const linkedinSlug = linkedinRaw
                                ? linkedinRaw.replace(/^https?:\/\//, '').replace(/^(www\.)?linkedin\.com\/(in|company)\//, '').replace(/\/+$/, '').split('/').pop() || null
                                : null;
                              const linkedinHref = linkedinRaw
                                ? (linkedinRaw.startsWith('http') ? linkedinRaw : `https://${linkedinRaw}`)
                                : null;
                              return (
                                <li key={name} className="text-sm text-foreground py-0.5 list-disc ml-4">
                                  <div>
                                    <span className="inline-flex items-center flex-wrap gap-x-1.5">
                                      <button onClick={() => person && handlePersonClick(person as any)} className="font-medium hover:underline cursor-pointer">{name}</button>
                                      {person?.title && <span className="text-muted-foreground">— {person.title}</span>}
                                    </span>
                                    {(person?.location || linkedinSlug || person?.email) && (
                                      <div className="ml-4 mt-1 mb-0.5 pl-2 border-l-2 border-muted/40 flex flex-col gap-0.5 text-xs text-muted-foreground">
                                        {person?.location && (
                                          <span className="inline-flex items-center gap-1">
                                            <MapPin className="h-3 w-3 shrink-0" />{person.location}
                                          </span>
                                        )}
                                        {linkedinSlug && linkedinHref && (
                                          <a href={linkedinHref} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-accent hover:underline">
                                            <LinkedInIcon className="h-3 w-3 shrink-0" />/{linkedinSlug}
                                          </a>
                                        )}
                                        {person?.email && (
                                          <a href={`mailto:${person.email}`} className="inline-flex items-center gap-1 text-accent hover:underline">
                                            <Mail className="h-3 w-3 shrink-0" />{person.email}
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </li>
                              );
                            };

                            return (
                              <div className="space-y-3">
                                {Array.from(grouped.entries()).map(([companyName, members]) => {
                                  const companyDomain = members[0]?.company ? (('domain' in members[0].company ? (members[0].company as any).domain : (members[0].company as any).website) ?? null) : null;
                                  return (
                                  <div key={companyName}>
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <CompanyLogo name={companyName} domain={companyDomain} size="sm" />
                                      {members[0]?.company ? (
                                        <button onClick={() => handleCompanyClick(members[0].company)} className="text-sm font-semibold text-foreground hover:underline cursor-pointer">{companyName}</button>
                                      ) : (
                                        <p className="text-sm font-semibold text-foreground">{companyName}</p>
                                      )}
                                    </div>
                                    <ul className="space-y-1 pl-1">
                                      {members.map(renderAttendee)}
                                    </ul>
                                  </div>
                                )})}
                                {internalGroup.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <CompanyLogo name={internalGroup.find(a => a.person?.company)?.person?.company || workspaceName || t("meetings.internalTeam")} domain={workspaceDomain || null} size="sm" />
                                      <p className="text-sm font-semibold text-foreground">{internalGroup.find(a => a.person?.company)?.person?.company || workspaceName || t("meetings.internalTeam")}</p>
                                    </div>
                                    <ul className="space-y-1 pl-1">
                                      {internalGroup.map(renderAttendee)}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </section>

                        {/* ── Companies (external only) ── */}
                        {(() => {
                          // The brief's Companies array only contains external companies
                          // (the prompt is fed only external accounts), so use it as the
                          // allowlist when present. Falls back to a domain-based filter
                          // when no brief data is available.
                          const hasBriefCompanies = Object.keys(briefCompanies.byName).length > 0;
                          const externalCompanies = uniqueCompanies.filter(c => {
                            if (hasBriefCompanies) {
                              return !!lookupBriefCompany(c);
                            }
                            const domain = 'domain' in c ? (c as any).domain : (c as any).website;
                            if (!domain || !workspaceDomain) return true;
                            return domain.toLowerCase() !== workspaceDomain.toLowerCase();
                          });
                          if (externalCompanies.length === 0) return null;
                          return (
                            <section>
                              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("meetings.companies")}</h4>
                              <div className="space-y-4">
                                {externalCompanies.map(c => {
                                  // Prefer the brief's company data: rich description + recent updates.
                                  // Fall back to the company object's description, then the generic
                                  // "X is a company in the technology space." placeholder.
                                  const briefCompany = lookupBriefCompany(c);
                                  const description = briefCompany?.description
                                    || c.description
                                    || t("meetings.companyDescriptionFallback", { name: c.name, industry: c.industry || t("meetings.industryFallback") });
                                  const updates = briefCompany?.updates || [];
                                  return (
                                    <div key={c.id} className="space-y-1.5">
                                      <div className="flex items-center gap-2">
                                        <CompanyLogo name={c.name} domain={('domain' in c ? (c as any).domain : (c as any).website) ?? null} size="sm" />
                                        <button onClick={() => handleCompanyClick(c)} className="text-sm font-semibold text-foreground hover:underline cursor-pointer">{c.name}</button>
                                      </div>
                                      <div className="pl-1 space-y-2">
                                        {companyIntelligence[c.name] ? (
                                          <p className="text-muted-foreground text-xs leading-relaxed">
                                            <Sparkles className="h-3 w-3 inline-block mr-1 text-accent shrink-0 -mt-0.5" />
                                            {companyIntelligence[c.name]}
                                          </p>
                                        ) : (
                                          <p className="text-muted-foreground text-xs leading-relaxed">
                                            <Sparkles className="h-3 w-3 inline-block mr-1 text-accent shrink-0 -mt-0.5" />
                                            {description}
                                            {c.website && !briefCompany?.description && (
                                              <> {t("meetings.learnMoreAt")} <a href={`https://${c.website}`} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{c.website}</a>.</>
                                            )}
                                          </p>
                                        )}
                                        {updates.length > 0 && (
                                          <div className="space-y-1">
                                            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t("meetings.recentUpdates")}</p>
                                            <ul className="space-y-1">
                                              {updates.map((u, i) => (
                                                <li key={i} className="text-muted-foreground text-xs leading-relaxed flex items-start gap-1.5">
                                                  <TrendingUp className="h-3 w-3 inline-block mt-1 text-accent shrink-0" />
                                                  <span>{u}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </section>
                          );
                        })()}

                        {/* ── Background (editable) ── */}
                        <BackgroundSection
                          useBackendData={!!useBackendData}
                          aiBrief={aiBrief}
                          briefSections={briefSections}
                          attendeeDetails={attendeeDetails}
                          workspaceName={workspaceName}
                          emailSummaries={emailSummaries}
                          meetingTranscripts={meetingTranscripts}
                          onPersonClick={handlePersonClick}
                          onCompanyClick={handleCompanyClick}
                          onMeetingClick={handleMeetingClick}
                          onEngagementClick={(engagementId, label) => {
                            // Open the referenced past meeting in a stacked briefing sheet.
                            // Minimal payload — the nested sheet fetches the full engagement
                            // via GraphQL using the id.
                            setLinkedMeeting({
                              id: engagementId,
                              title: label,
                              date: "",
                              time: "",
                              duration: "",
                              type: "",
                              attendees: [],
                            });
                            setLinkedMeetingOpen(true);
                          }}
                        />


                        {/* ── Template Selector (only when custom templates exist) ── */}
                        {briefingTemplates.length > 0 && (
                          <section className="pt-4 border-t border-border">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("meetings.briefingTemplate")}</h4>
                            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                              <SelectTrigger className="w-full h-9 text-sm">
                                <SelectValue placeholder={t("meetings.templatePlaceholder")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="default">{t("meetings.defaultBriefing")}</SelectItem>
                                {briefingTemplates.map(t => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </section>
                        )}

                      </div>
                    </ScrollArea>
                  )}
                </>
              )}
                  </>
                );
              })()}
              </EngagementPrivacyGate>
            </>
          )}
        </SheetContent>
      </Sheet>

      <RecordingOverrideDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        onConfirm={() => { void sendRecording(true, true); }}
      />

      {/* PersonDetailSheet - stacked on top of briefing sheet */}
      <PersonDetailSheet
        open={personSheetOpen}
        onOpenChange={setPersonSheetOpen}
        person={selectedPerson}
        hideOverlay={true}
        onBack={() => setPersonSheetOpen(false)}
      />

      {/* CompanyDetailSheet - stacked on top of briefing sheet */}
      <CompanyDetailSheet
        open={companySheetOpen}
        onOpenChange={setCompanySheetOpen}
        company={selectedCompany}
        hideOverlay={true}
        onBack={() => setCompanySheetOpen(false)}
      />

      <AskCoffeePanel
        open={askCoffeePanelOpen}
        onOpenChange={(open) => {
          setAskCoffeePanelOpen(open);
          if (!open) {
            // Defer clearing query to allow panel to fully unmount
            setTimeout(() => setAskCoffeeQuery(""), 100);
          }
        }}
        initialQuery={askCoffeeQuery}
        initialContext={useBackendData && engagement?.id ? { type: "meeting" as const, id: engagement.id } : undefined}
        contextTitle={meeting?.title}
        contextSubtitle={meeting ? `${meeting.date} · ${meeting.time} · ${meeting.duration}` : undefined}
        onBack={() => setAskCoffeePanelOpen(false)}
      />

      {/* MeetingIntelligenceSheet - stacked for linked meeting references */}
      {linkedMeeting && (
        <MeetingIntelligenceSheet
          open={linkedMeetingOpen}
          onOpenChange={setLinkedMeetingOpen}
          note={{
            id: linkedMeeting.id,
            name: linkedMeeting.title,
            company: "",
            people: linkedMeeting.attendees,
            date: linkedMeeting.date,
          }}
          hideOverlay={true}
          onBack={() => setLinkedMeetingOpen(false)}
        />
      )}

    </>
  );
}
